import { serverTimestamp } from 'firebase/database';
import { createWithKey, updateData, updateMultipleSafe } from './api-wrapper.service';
import logger from '../../utils/logger';

/**
 * Generates a unique ID for tracking purposes
 */
const generateUniqueId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${randomStr}`;
};

/**
 * Represents an action that was performed while offline
 */
export interface PendingAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  path: string;
  data: any;
  timestamp: number;
  deviceId: string;
  retryCount: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  /** Stamped when status flips to 'processing' — lets the loader reclaim
   *  records stranded mid-run (app killed / WebView purged). */
  processingAt?: number;
}

/** A 'processing' record older than this is considered stranded (the run
 *  that owned it died) and is safe to retry. */
const PROCESSING_STALE_MS = 2 * 60 * 1000;

/**
 * Generates a unique device identifier or retrieves the existing one
 */
const getDeviceId = (): string => {
  const storageKey = 'marune_device_id';
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    deviceId = generateUniqueId();
    localStorage.setItem(storageKey, deviceId);
  }
  
  return deviceId;
};

/**
 * Stores a pending action to be processed when back online
 * 
 * @param type The type of action (create, update, delete)
 * @param path The database path this action affects
 * @param data The data for this action
 * @returns The ID of the pending action
 */
export const storePendingAction = (
  type: 'create' | 'update' | 'delete',
  path: string,
  data: any
): string => {
  try {
    const pendingActionId = generateUniqueId();
    const deviceId = getDeviceId();
    
    // Create pending action object
    const pendingAction: PendingAction = {
      id: pendingActionId,
      type,
      path,
      data,
      timestamp: Date.now(),
      deviceId,
      retryCount: 0,
      status: 'pending'
    };
    
    // Store in local storage first as backup
    const storageKey = `pending_action_${pendingActionId}`;
    localStorage.setItem(storageKey, JSON.stringify(pendingAction));
    
    // Also add to pending actions in IndexedDB or other storage if available
    // This is a placeholder for potential IndexedDB implementation
    
    logger.info(`Stored pending action ${pendingActionId} (${type}) for path: ${path}`);
    
    return pendingActionId;
  } catch (error: any) {
    logger.error('Error storing pending action:', error);
    return '';
  }
};

/**
 * Reentrancy guard: processPendingActions has multiple entry points
 * (SyncManager network-change, initialize, 60s self-retry). Without this
 * flag a second run could start while completed actions are still awaiting
 * their delayed cleanup and replay them, duplicating entities.
 */
let isProcessingPendingActions = false;

/**
 * Processes all pending actions when app comes back online
 * Enhanced with better error handling, retry logic, and IndexedDB fallback
 *
 * @param uid The user ID
 * @returns A promise that resolves when all pending actions are processed
 */
export const processPendingActions = async (uid: string): Promise<void> => {
  // No-op if a run is already in flight - overlapping runs replay actions
  if (isProcessingPendingActions) {
    logger.info('Pending actions already being processed, skipping duplicate run');
    return;
  }
  isProcessingPendingActions = true;

  try {
    logger.info('Processing pending actions after reconnection');
    
    // Dispatch event for applications to hook into sync process
    window.dispatchEvent(new CustomEvent('marune:sync-started'));
    
    // Get all pending actions from local storage
    const pendingActions: PendingAction[] = [];
    
    // Find all keys in localStorage that start with pending_action_
    // Collect keys first so removing stale records below doesn't shift
    // localStorage indices mid-iteration
    const pendingActionKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('pending_action_')) {
        pendingActionKeys.push(key);
      }
    }

    for (const key of pendingActionKeys) {
      try {
        const actionJson = localStorage.getItem(key);
        if (actionJson) {
          const action = JSON.parse(actionJson) as PendingAction;

          // Skip records already handled: 'completed' ones only linger until
          // their delayed cleanup fires, and 'processing' ones belong to a run
          // in flight - replaying either would duplicate the write
          if (action.status === 'completed') {
            // Clean up stale completed records immediately
            localStorage.removeItem(key);
            continue;
          }
          if (action.status === 'processing') {
            // Crash recovery: a 'processing' record whose owning run died
            // (app killed, WebView purged) would otherwise be skipped
            // FOREVER, silently dropping the user's offline write. Reclaim
            // it once the stale window passes (or when it predates the
            // processingAt stamp entirely).
            const stale =
              !action.processingAt ||
              Date.now() - action.processingAt > PROCESSING_STALE_MS;
            if (!stale) {
              continue;
            }
            logger.warn(`Reclaiming stranded 'processing' action ${action.id}`);
            action.status = 'pending';
          }

          pendingActions.push(action);
        }
      } catch (err) {
        logger.error('Error parsing pending action:', err);
      }
    }
    
    // Sort actions by timestamp to ensure they're processed in chronological order
    pendingActions.sort((a, b) => a.timestamp - b.timestamp);
    
    // Group actions by entity to handle them more intelligently
    const actionsByEntity: Record<string, PendingAction[]> = {};
    for (const action of pendingActions) {
      const entityId = action.path.split('/').pop() || action.path; // Extract entity ID from path
      if (!actionsByEntity[entityId]) {
        actionsByEntity[entityId] = [];
      }
      actionsByEntity[entityId].push(action);
    }
    
    logger.info(`Found ${pendingActions.length} pending actions to process (${Object.keys(actionsByEntity).length} affected entities)`);
    
    // Process actions for each entity in order
    const results: Record<string, { success: boolean; error?: string }> = {};
    
    for (const [entityId, entityActions] of Object.entries(actionsByEntity)) {
      try {
        logger.info(`Processing ${entityActions.length} actions for entity ${entityId}`);
        
        // Optimize by collapsing multiple updates on the same entity
        if (entityActions.length > 1 && entityActions.every(a => a.type === 'update')) {
          logger.info(`Optimizing: collapsing ${entityActions.length} updates into one for entity ${entityId}`);
          
          // Take the latest action as our base
          const latestAction = entityActions.reduce((latest, current) => 
            current.timestamp > latest.timestamp ? current : latest
          );
          
          // Update state for all actions
          for (const action of entityActions) {
            action.status = 'processing';
            action.processingAt = Date.now();
            localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
          }

          // Process the optimized action. Guarded like the per-action branch
          // below: an unguarded throw here used to leave every collapsed
          // record stranded as 'processing' (and therefore never retried).
          try {
            await handleUpdateAction(latestAction, uid);

            // Mark all as completed
            for (const action of entityActions) {
              action.status = 'completed';
              localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));

              // After successful processing, remove from storage
              setTimeout(() => {
                localStorage.removeItem(`pending_action_${action.id}`);
              }, 5000); // Keep for a few seconds just in case
            }

            results[entityId] = { success: true };
          } catch (collapseError: any) {
            logger.error(`Error processing collapsed update for entity ${entityId}:`, collapseError);
            // Re-queue (or fail out) every record so the next run retries them
            for (const action of entityActions) {
              if (action.retryCount >= 3) {
                action.status = 'failed';
              } else {
                action.retryCount++;
                action.status = 'pending';
              }
              localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
            }
            results[entityId] = {
              success: false,
              error: collapseError instanceof Error ? collapseError.message : 'Unknown error',
            };
          }
        } else {
          // Process each action normally
          for (const action of entityActions) {
            try {
              // Update status to processing
              action.status = 'processing';
              action.processingAt = Date.now();
              localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
              
              // Process based on action type
              switch (action.type) {
                case 'create':
                  await handleCreateAction(action, uid);
                  break;
                case 'update':
                  await handleUpdateAction(action, uid);
                  break;
                case 'delete':
                  await handleDeleteAction(action, uid);
                  break;
              }
              
              // Mark as completed
              action.status = 'completed';
              localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
              
              // After successful processing, remove from storage
              setTimeout(() => {
                localStorage.removeItem(`pending_action_${action.id}`);
              }, 5000); // Keep for a few seconds just in case
              
              results[action.id] = { success: true };
            } catch (error: any) {
              logger.error(`Error processing pending action ${action.id}:`, error);
              
              results[action.id] = { 
                success: false, 
                error: error instanceof Error ? error.message : 'Unknown error'
              };
              
              // Mark as failed if too many retries
              if (action.retryCount >= 3) {
                action.status = 'failed';
                localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
              } else {
                // Increment retry count and RE-QUEUE: leaving the status as
                // 'processing' here made the loader skip the record on every
                // subsequent run — one transient error silently dropped the
                // offline write forever.
                action.retryCount++;
                action.status = 'pending';
                localStorage.setItem(`pending_action_${action.id}`, JSON.stringify(action));
              }
            }
          }
        }
      } catch (entityError: any) {
        logger.error(`Error processing entity ${entityId}:`, entityError);
        results[entityId] = { 
          success: false, 
          error: entityError instanceof Error ? entityError.message : 'Unknown entity processing error'
        };
      }
    }
    
    // Summary of processing results
    const successCount = Object.values(results).filter(r => r.success).length;
    const failureCount = Object.values(results).filter(r => !r.success).length;
    
    logger.info(`Finished processing pending actions: ${successCount} succeeded, ${failureCount} failed`);
    
    // Dispatch event for applications to hook into sync completion
    window.dispatchEvent(new CustomEvent('marune:sync-completed', { 
      detail: { 
        success: failureCount === 0,
        successCount,
        failureCount,
        results
      } 
    }));
    
    // If there were failures that will be retried, schedule another attempt
    const retryableFailures = pendingActions.filter(
      action => action.status !== 'completed' && action.retryCount < 3
    );
    
    if (retryableFailures.length > 0) {
      logger.info(`Scheduling retry for ${retryableFailures.length} actions in 60 seconds`);
      setTimeout(() => {
        processPendingActions(uid).catch(e => 
          logger.error('Error in scheduled retry of pending actions:', e)
        );
      }, 60000); // Try again in 60 seconds
    }
  } catch (error: any) {
    logger.error('Error processing pending actions:', error);
    
    // Dispatch error event
    window.dispatchEvent(new CustomEvent('marune:sync-error', {
      detail: { error: error instanceof Error ? error.message : 'Unknown error processing pending actions' }
    }));
  } finally {
    // Release the reentrancy guard so later runs (including the scheduled
    // 60s retry) can proceed
    isProcessingPendingActions = false;
  }
};

/**
 * Handles a pending create action
 */
const handleCreateAction = async (action: PendingAction, uid: string): Promise<void> => {
  // Generate server timestamp for creation
  const data = {
    ...action.data,
    lastModified: serverTimestamp(),
    modifiedBy: uid,
    offline: true,
    offlineGeneratedAt: action.timestamp
  };
  
  // Use createWithKey from api-wrapper which ensures database is online
  await createWithKey(action.path, data);
  
  logger.info(`Processed create action for path: ${action.path}`);
};

/**
 * Handles a pending update action
 */
const handleUpdateAction = async (action: PendingAction, uid: string): Promise<void> => {
  // Add metadata for offline update
  const data = {
    ...action.data,
    lastModified: serverTimestamp(),
    modifiedBy: uid,
    offlineModified: true,
    offlineModifiedAt: action.timestamp
  };
  
  // Use updateData from api-wrapper which ensures database is online
  await updateData(action.path, data);
  
  logger.info(`Processed update action for path: ${action.path}`);
};

/**
 * Handles a pending delete action
 */
const handleDeleteAction = async (action: PendingAction, uid: string): Promise<void> => {
  // For deletes, we use updateMultipleSafe with null
  // This follows Firebase's pattern for deleting nodes
  const updates: Record<string, any> = {};
  updates[action.path] = null;
  
  // Use updateMultipleSafe from api-wrapper which ensures database is online
  await updateMultipleSafe(updates);
  
  logger.info(`Processed delete action for path: ${action.path}`);
};

const exportedObject = {
  storePendingAction,
  processPendingActions
};

export default exportedObject;